import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { format } from 'date-fns';
import {
  applyTypography,
  Input,
  BaseButton,
  CustomDatePicker,
  useIsMobile,
  isValidEmailFormat,
  useAuth,
  KoreanSkillsList,
  TopikList,
  TopikSpeakingList,
  useT,
} from '@repo/common';

import { LocationAutoCompleteList } from '@repo/common';
import { translateTexts } from '../../../../../api/career/career.api';
import { useLocationSelection } from '@repo/common';
import NationalityInputBlock from './DynamicFields/NationalityInputBlock';
import EmailInputBlock from './DynamicFields/EmailInputBlock';
import AddPersonalInfoModal from './AddPersonalInfoModal';
import CareerList from './CareerList';
import PhoneInputBlock from './DynamicFields/PhoneInputBlock';
import TopikCertModal from './TopikCertModal';
import { FIELD_COMPONENT_MAP } from './DynamicFields/Index';

import { ReactComponent as CalendarIcon } from '@repo/common/assets/icons/16px/icon_16px_calendar_outline.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-top.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';
import { ReactComponent as LocationIcon } from '@repo/common/assets/icons/16px/icon_16px_location.svg';
import { ReactComponent as EditIcon } from '@repo/common/assets/icons/20px/icon_20px_edit_filled.svg';
import { ReactComponent as TrashIcon } from '@repo/common/assets/icons/20px/icon_20px_delete_outline.svg';

const TXT_DEFAULT_KEY = 'b16';

const StyledCalendarIcon = ({ $state, className }) => {
  const iconClasses = classNames(className, {
    'text-icon-primary': $state === 'active',
    'text-icon-placeholder': $state === 'focused',
    'text-icon-secondary': $state === 'default',
  });
  return <CalendarIcon className={iconClasses} />;
};

const StyledLocationIcon = ({ $state }) => (
  <LocationIcon
    className={classNames({
      'text-icon-primary': $state === 'active',
      'text-icon-placeholder': $state === 'focused',
      'text-icon-secondary': $state === 'default',
    })}
  />
);

const getInputState = (focused, clicked) => {
  if (clicked) {
    return 'active';
  }
  if (focused) {
    return 'focused';
  }
  return 'default';
};

const getIconState = (value, fieldState) => {
  if (value instanceof Date || value) return 'active';
  if (fieldState === 'active' || fieldState === 'focused') return 'focused';
  return 'default';
};

const createFieldInstance = (type) => {
  const base = {
    id: crypto.randomUUID(),
    type,
    value: '',
    focused: false,
    clicked: false,
    inputRef: React.createRef(),
  };

  if (type === 'Phone') {
    return {
      ...base,
      value: {
        number: '',
        countryCode: '+82',
        iso: 'KR',
        countryName: 'South Korea',
      },
      isPhoneActive: false,
      selectedCountry: {
        name: 'South Korea',
        code: '+82',
        iso: 'KR',
      },
    };
  }

  return base;
};

const CAREER_OPTIONS = [
  { value: 'Select', label: 'Select Career level', labelKr: '경력 수준 선택' },
  { value: 'Entry', label: 'Entry (0 yrs)', labelKr: '신입 (0년)' },
  { value: '1 yrs', label: '1 yrs', labelKr: '1년' },
  { value: '2 yrs', label: '2 yrs', labelKr: '2년' },
  { value: '3 yrs', label: '3 yrs', labelKr: '3년' },
  { value: '4 yrs', label: '4 yrs', labelKr: '4년' },
  { value: '5 yrs', label: '5 yrs', labelKr: '5년' },
  { value: '6 yrs', label: '6 yrs', labelKr: '6년' },
  { value: '7 yrs', label: '7 yrs', labelKr: '7년' },
  { value: '8+ yrs', label: '8+ yrs', labelKr: '8년 이상' },
];

const PersonalInfoForm = ({
  profile: initialProfile,
  setResumeData,
  $isMobile,
}) => {
  const t = useT();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { resumeId } = useParams();

  const [selectedCountry, setSelectedCountry] = useState({
    name: 'South Korea',
    code: '+82',
    iso: 'KR',
  });
  const [topikInfo, setTopikInfo] = useState(null);

  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: [],
    birthday: null,
    location: '',
    nationality: '',
    phone: [],
    koreanSkills: '',
    career: '',
    targetRole: [],
    koreanName: '',
    koreanNameAuto: true,
  });

  const [firstNameFocused, setFirstNameFocused] = useState(false);
  const [lastNameFocused, setLastNameFocused] = useState(false);
  const [locationFocused, setLocationFocused] = useState(false);
  const [birthdayFocused, setBirthdayFocused] = useState(false);
  const [koreanSkillsFocused, setKoreanSkillsFocused] = useState(false);
  const [topikDateFocused, setTopikDateFocused] = useState(false);
  const [topikSpeakingDateFocused, setTopikSpeakingDateFocused] =
    useState(false);
  const [nationalityFocused, setNationalityFocused] = useState(false);
  const [phoneFocused, setPhoneFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [koreanNameFocused, setKoreanNameFocused] = useState(false);
  const [careerFocused, setCareerFocused] = useState(false);
  const [emailStatus, setEmailStatus] = useState('default');

  const formContainerRef = useRef(null);
  const locationInputRef = useRef(null);
  const birthdayInputRef = useRef(null);
  const koreanSkillsInputRef = useRef(null);
  const topikInputRef = useRef(null);
  const topikSpeakingInputRef = useRef(null);
  const nationalityInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const careerInputRef = useRef(null);
  const translateTimeoutRef = useRef(null);

  const [showKoreanSkillsList, setShowKoreanSkillsList] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isKoreanSkills, setIsKoreanSkills] = useState(false);
  const [isTopikOpen, setIsTopikOpen] = useState(false);
  const [isTopikSpeakingOpen, setIsTopikSpeakingOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCareerOpen, setIsCareerOpen] = useState(false);
  const [isPhoneActive, setIsPhoneActive] = useState(false);
  const [topikModalOpen, setTopikModalOpen] = useState(false);

  const [birthdayClicked, setBirthdayClicked] = useState(false);
  const [koreanSkillsClicked, setKoreanSkillsClicked] = useState(false);
  const [careerClicked, setCareerClicked] = useState(false);

  const hasBirthdayValue = Boolean(profile.birthday);

  const [dynamicFields, setDynamicFields] = useState([]);

  const effectiveFirstNameStatus = getInputState(
    profile.firstName,
    firstNameFocused,
  );
  const effectiveLastNameStatus = getInputState(
    profile.lastName,
    lastNameFocused,
  );
  const effectiveLocationStatus = getInputState(
    profile.location,
    locationFocused,
  );
  const locationIconState = getIconState(
    profile.location,
    effectiveLocationStatus,
  );
  const effectiveBirthdayStatus = getInputState(
    birthdayFocused,
    birthdayClicked,
  );
  const birthdayIconState = getIconState(
    profile.birthday,
    effectiveBirthdayStatus,
  );
  const effectiveKoreanSkillsStatus = getInputState(
    koreanSkillsFocused,
    koreanSkillsClicked,
  );
  const effectiveTopikDateStatus = getInputState(
    topikInfo?.date ?? '',
    topikDateFocused,
  );
  const effectiveTopikSpeakingDateStatus = getInputState(
    topikInfo?.date ?? '',
    topikSpeakingDateFocused,
  );
  const effectiveNationalityStatus = getInputState(
    profile.nationality,
    nationalityFocused,
  );
  const effectivePhoneStatus = getInputState(
    profile.phone?.[0]?.number,
    phoneFocused,
  );
  const effectiveEmailStatus = getInputState(profile.email, emailFocused);
  const effectiveKoreanNameStatus = getInputState(
    profile.koreanName,
    koreanNameFocused,
  );
  const effectiveCareerStatus = getInputState(
    profile.career,
    careerFocused,
    careerClicked,
  );

  const closeAllPopups = useCallback(() => {
    setIsDatePickerOpen(false);
    setLocationFocused(false);
    setBirthdayClicked(false);
    setBirthdayFocused(false);
    setKoreanSkillsClicked(false);
    setKoreanSkillsFocused(false);
    setShowKoreanSkillsList(false);
    setIsTopikOpen(false);
    setIsTopikSpeakingOpen(false);
    setNationalityFocused(false);
    setCareerFocused(false);
  }, []);

  const handleBirthdayClick = (e) => {
    e.stopPropagation();
    closeAllPopups();
    setBirthdayClicked(true);
    setBirthdayFocused(true);
    setIsDatePickerOpen(true);
  };

  const handleManualLocationEntry = (currentQuery) => {
    updateProfile('location', currentQuery);
    setLocationFocused(false);
  };

  const handleLocationSelect = (item) => {
    updateProfile(
      'location',
      `${item.name}${item.country ? `, ${item.country}` : ''}`,
    );
    setLocationFocused(false);
  };

  const location = useLocationSelection({
    value: profile.location,
    onChange: (e) => updateProfile('location', e.target.value),
    onFocus: () => {
      closeAllPopups();
      setLocationFocused(true);
    },
    onBlur: () => setLocationFocused(false),
    onSelect: handleLocationSelect,
    onManualEnter: handleManualLocationEntry,
  });

  const handleKoreanSkillsClick = (e) => {
    e.stopPropagation();
    if (showKoreanSkillsList) {
      setShowKoreanSkillsList(false);
      setKoreanSkillsFocused(false);
      setKoreanSkillsClicked(false);
      return;
    }
    closeAllPopups();
    setKoreanSkillsClicked(true);
    setKoreanSkillsFocused(true);
    setShowKoreanSkillsList(true);
  };

  const handlePhoneChange = (e) => {
    setProfile((prev) => {
      const newPhone = [...prev.phone];

      if (!newPhone[0]) {
        newPhone[0] = {
          number: '',
          countryCode: selectedCountry.code,
          iso: selectedCountry.iso,
          countryName: selectedCountry.name,
        };
      }

      newPhone[0] = { ...newPhone[0], number: e.target.value };

      return {
        ...prev,
        phone: newPhone,
      };
    });
  };

  const handleCareerClick = (e) => {
    e.stopPropagation();
    if (isCareerOpen) {
      setIsCareerOpen(false);
      setCareerFocused(false);
      setCareerClicked(false);
      return;
    }
    closeAllPopups();
    setCareerFocused(true);
    setCareerClicked(true);
    setIsCareerOpen(true);
  };

  const handleTopikClick = (e) => {
    e.stopPropagation();
    if (isTopikOpen) {
      setIsTopikOpen(false);
      return;
    }
    closeAllPopups();
    setIsTopikOpen(true);
  };

  const handleTopikSpeakingClick = (e) => {
    e.stopPropagation();
    if (isTopikSpeakingOpen) {
      setIsTopikSpeakingOpen(false);
      return;
    }
    closeAllPopups();
    setIsTopikSpeakingOpen(true);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    updateProfile('email', value);

    if (!value) {
      setEmailStatus('default');
      return;
    }

    if (!isValidEmailFormat(value)) {
      setEmailStatus('warning');
      return;
    }

    setEmailStatus('success');
  };

  const addPersonalInfo = () => {
    closeAllPopups();
    setIsModalOpen(true);
  };

  const updateDynamicField = (id, updates) =>
    setDynamicFields((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...updates } : f)),
    );

  const updateProfile = (key, value) =>
    setProfile((prev) => ({ ...prev, [key]: value }));

  const parseProfileData = (initialProfile) => {
    if (!initialProfile) return { profile: null, dynamicFields: [] };
    const dynamicFields = [];

    const splitField = (arr, type, allDynamic = false) => {
      if (!Array.isArray(arr) || arr.length === 0)
        return { main: '', rest: [] };

      const [main, ...rest] = arr;
      const toDynamic = allDynamic ? [main, ...rest] : rest;

      toDynamic.forEach((value) => {
        dynamicFields.push({
          id: crypto.randomUUID(),
          type,
          value,
          focused: false,
          clicked: false,
          inputRef: React.createRef(),
          ...(type === 'Phone' && {
            isPhoneActive: false,
            selectedCountry: {
              name: value.countryName,
              code: value.countryCode,
              iso: value.iso,
            },
          }),
        });
      });

      return { main: allDynamic ? '' : main };
    };

    const email = splitField(initialProfile.email, 'E-mail', false);
    const phone = splitField(initialProfile.phone, 'Phone', false);
    const nationality = splitField(
      initialProfile.nationality,
      'Nationality',
      false,
    );
    const targetRole = splitField(
      initialProfile.targetRole,
      'Target Role',
      true,
    );

    return {
      profile: {
        firstName: initialProfile.firstName ?? '',
        lastName: initialProfile.lastName ?? '',
        location: initialProfile.location ?? '',
        birthday: initialProfile.birthday
          ? new Date(initialProfile.birthday)
          : null,
        email: email.main || '',
        phone: phone.main ? [phone.main] : [],
        nationality: nationality.main || '',
        targetRole: targetRole.main || '',
        career: initialProfile.career ?? '',
        koreanSkills: initialProfile.koreanSkills ?? '',
        koreanName: initialProfile.koreanName ?? '',
        koreanNameAuto: initialProfile.koreanNameAuto ?? true,
      },
      dynamicFields,
    };
  };

  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) return;

    const isNewResume = !resumeId;

    if (isNewResume) {
      if (
        initialProfile &&
        Object.keys(initialProfile).length > 0 &&
        initialProfile.firstName
      ) {
        const { profile: parsedProfile, dynamicFields: parsedDynamics } =
          parseProfileData(initialProfile);
        setProfile(parsedProfile);
        setDynamicFields(parsedDynamics);
        isInitialized.current = true;
        return;
      }

      if (!user?.firstName) return;

      setProfile((prev) => ({
        ...prev,
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email || user.socialEmail || '',
        location: user.location ?? '',
        nationality: user.nationality ?? '',
        koreanSkills: user.koreanSkills ?? '',
      }));
      isInitialized.current = true;
    } else {
      if (!initialProfile?.firstName) return;

      const { profile: parsedProfile, dynamicFields: parsedDynamics } =
        parseProfileData(initialProfile);
      setProfile(parsedProfile);
      setDynamicFields(parsedDynamics);
      isInitialized.current = true;
    }
  }, [initialProfile, user, resumeId]);

  const isTopikInitialized = useRef(false);

  useEffect(() => {
    if (!initialProfile?.topik || isTopikInitialized.current) return;
    setTopikInfo({
      type: initialProfile.topik.type,
      level: initialProfile.topik.level ?? '',
      date: initialProfile.topik.date ?? '',
    });
    isTopikInitialized.current = true;
  }, [initialProfile]);

  useEffect(() => {
    if (isPhoneActive) {
      phoneInputRef.current?.focus?.();
    }
  }, [isPhoneActive]);

  useEffect(() => {
    if (!isCareerOpen && !isTopikOpen && !isTopikSpeakingOpen) return;
    const handleClickOutside = (e) => {
      if (
        isCareerOpen &&
        careerInputRef.current &&
        !careerInputRef.current.contains(e.target)
      ) {
        setIsCareerOpen(false);
        setCareerFocused(false);
        setCareerClicked(false);
      }
      if (
        isTopikOpen &&
        topikInputRef.current &&
        !topikInputRef.current.contains(e.target)
      ) {
        setIsTopikOpen(false);
      }
      if (
        isTopikSpeakingOpen &&
        topikSpeakingInputRef.current &&
        !topikSpeakingInputRef.current.contains(e.target)
      ) {
        setIsTopikSpeakingOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCareerOpen, isTopikOpen, isTopikSpeakingOpen]);

  useEffect(() => {
    if (!profile.koreanNameAuto || (!profile.firstName && !profile.lastName))
      return;
    const fullName = `${profile.lastName} ${profile.firstName}`.trim();
    if (!fullName) return;
    if (translateTimeoutRef.current) clearTimeout(translateTimeoutRef.current);
    translateTimeoutRef.current = setTimeout(async () => {
      try {
        const [translated] = await translateTexts([fullName]);
        if (!translated || translated === fullName) return;
        setProfile((prev) =>
          prev.koreanName === translated
            ? prev
            : { ...prev, koreanName: translated },
        );
      } catch (e) {
        console.error('name translate error', e);
      }
    }, 1000);
    return () =>
      translateTimeoutRef.current && clearTimeout(translateTimeoutRef.current);
  }, [profile.firstName, profile.lastName, profile.koreanNameAuto]);

  useEffect(() => {
    dynamicFields.forEach((f) => {
      if (f.isPhoneActive && f.inputRef.current) {
        f.inputRef.current.focus?.();
      }
    });
  }, [dynamicFields]);

  useEffect(() => {
    const { profileUrl, ...restProfile } = profile;
    const dynamicTargetRole = profile.targetRole ? [profile.targetRole] : [];
    const dynamicNationalities = profile.nationality
      ? [profile.nationality]
      : [];
    const dynamicPhone = [...profile.phone];
    const dynamicEmails = profile.email ? [profile.email] : [];
    dynamicFields.forEach((f) => {
      if (!f.value) return;
      switch (f.type) {
        case 'Target Role':
          dynamicTargetRole.push(f.value);
          break;
        case 'Nationality':
          dynamicNationalities.push(f.value);
          break;
        case 'Phone':
          dynamicPhone.push(f.value);
          break;
        case 'E-mail':
          dynamicEmails.push(f.value);
          break;
        default:
          break;
      }
    });
    const finalData = {
      ...restProfile,
      targetRole: dynamicTargetRole,
      nationality: dynamicNationalities,
      phone: dynamicPhone,
      email: dynamicEmails,
    };
    const hasAnyValue =
      Object.values(profile).some((v) => v !== '' && v !== null) ||
      dynamicFields.length > 0;
    if (!hasAnyValue) return;
    setResumeData((prev) => ({
      ...prev,
      profile: {
        ...prev.profile,
        ...finalData,
      },
    }));
  }, [profile, selectedCountry, dynamicFields, setResumeData]);

  useEffect(() => {
    setResumeData((prev) => ({
      ...prev,
      profile: {
        ...prev.profile,
        topik: topikInfo
          ? {
              type: topikInfo.type,
              level: topikInfo.level,
              date: topikInfo.date,
            }
          : null,
      },
    }));
  }, [topikInfo, setResumeData]);

  return (
    <div
      ref={formContainerRef}
      className={classNames(
        'grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-3 md:gap-6 md:max-w-[776px]',
        emailStatus === 'warning' || emailStatus === 'success'
          ? 'items-center'
          : 'items-end',
      )}
    >
      <div className={classNames(applyTypography(TXT_DEFAULT_KEY))}>
        {/* First Name */}
        <div className='relative min-w-[260px] md:max-w-[380px] w-full'>
          <Input
            id='first-name-input'
            label={firstNameFocused ? t('First name*', '이름*') : ''}
            value={profile.firstName}
            state={effectiveFirstNameStatus}
            hideCount
            showStatusIcon={false}
            onChange={(e) => updateProfile('firstName', e.target.value)}
            onFocus={() => setFirstNameFocused(true)}
            onBlur={() => setFirstNameFocused(false)}
            placeholder=''
            className='placeholder:opacity-0 w-full md:max-w-[380px]'
          />
          {!profile.firstName && !firstNameFocused && (
            <span
              className={`absolute left-4 top-[25%] text-text-placeholder pointer-events-none`}
            >
              {t('First name', '이름')}
              <span className='text-text-accent ml-0.5'>*</span>
            </span>
          )}
        </div>
      </div>

      {/* Last Name */}
      <div className='relative min-w-[260px] md:max-w-[380px] w-full'>
        <Input
          id='last-name-input'
          label={lastNameFocused ? t('Last name', '성') : ''}
          value={profile.lastName}
          state={effectiveLastNameStatus}
          hideCount
          showStatusIcon={false}
          onChange={(e) => updateProfile('lastName', e.target.value)}
          onFocus={() => setLastNameFocused(true)}
          onBlur={() => setLastNameFocused(false)}
          placeholder=''
          className='placeholder:opacity-0 w-full md:max-w-[380px]'
        />
        {!profile.lastName && !lastNameFocused && (
          <span
            className={`absolute left-4 top-[25%] text-text-placeholder pointer-events-none`}
          >
            {t('Last name', '성')}
          </span>
        )}
      </div>

      {/* Birthday */}
      <div className='relative min-w-[260px] md:max-w-[380px] w-full'>
        <Input
          id='birthday-input'
          ref={birthdayInputRef}
          label={''}
          placeholder={
            birthdayFocused || isDatePickerOpen
              ? t('Select your birthday', '생년월일을 선택하세요')
              : ''
          }
          value={
            profile.birthday ? format(profile.birthday, 'dd MMM yyyy') : ''
          }
          state={effectiveBirthdayStatus}
          readOnly
          onClick={handleBirthdayClick}
          onChange={(date) => {
            updateProfile('birthday', date);
            setIsDatePickerOpen(false);
          }}
          hideCount
          showStatusIcon={false}
          rightIcon={<StyledCalendarIcon $state={birthdayIconState} />}
          onRightIconClick={handleBirthdayClick}
          onClose={() => setIsDatePickerOpen(false)}
          className='placeholder:opacity-0 w-full'
        />
        {!hasBirthdayValue && !profile.birthday && !birthdayFocused && (
          <span
            className={`absolute left-4 top-[25%] text-text-placeholder pointer-events-none`}
          >
            {t('Birthday', '생년월일')}
            <span className='text-text-accent ml-0.5'>*</span>
          </span>
        )}

        {isDatePickerOpen && (
          <CustomDatePicker
            selectedDate={profile.birthday}
            onChange={(date) => {
              updateProfile('birthday', date);
              setIsDatePickerOpen(false);
              setBirthdayClicked(false);
              setBirthdayFocused(false);
            }}
            $isMobile={isMobile}
            onClose={() => {
              setIsDatePickerOpen(false);
              setBirthdayClicked(false);
              setBirthdayFocused(false);
            }}
            onClickOutside={
              !isMobile
                ? () => {
                    setIsDatePickerOpen(false);
                    setBirthdayClicked(false);
                    setBirthdayFocused(false);
                  }
                : undefined
            }
          />
        )}
      </div>

      {/* Korean skills */}
      <div className='relative min-w-[260px] md:max-w-[380px] w-full'>
        {topikInfo && !isMobile && (
          <div className='hidden md:block absolute -inset-x-3 -top-3 -bottom-6 bg-green-light01 rounded-t-[20px] -z-10 pointer-events-none' />
        )}
        <Input
          id='korean-skills-input'
          ref={koreanSkillsInputRef}
          label={''}
          placeholder={t('Korean skills*', '한국어 능력*')}
          value={profile.koreanSkills}
          state={effectiveKoreanSkillsStatus}
          readOnly
          onClick={handleKoreanSkillsClick}
          onChange={(e) => updateProfile('koreanSkills', e.target.value)}
          onFocus={() => setKoreanSkillsFocused(true)}
          onBlur={() => {
            setKoreanSkillsFocused(false);
            setKoreanSkillsClicked(false);
            setShowKoreanSkillsList(false);
          }}
          hideCount
          showStatusIcon={false}
          rightIcon={
            showKoreanSkillsList ? (
              <ArrowTopIcon className='transition-transform duration-300' />
            ) : (
              <ArrowBottomIcon className='transition-transform duration-300' />
            )
          }
          onRightIconClick={handleKoreanSkillsClick}
          className='w-full'
        />

        {/* Korean Skills List */}
        {showKoreanSkillsList && (
          <div className='relative w-full mt-1.5 tabletH:absolute tabletH:top-[54px] tabletH:left-0 tabletH:mt-0 tabletH:w-auto z-10'>
            <KoreanSkillsList
              selectedOption={profile.koreanSkills}
              onSelect={(selectedKoreanSkills) => {
                updateProfile('koreanSkills', selectedKoreanSkills);
                setIsKoreanSkills(false);
                setShowKoreanSkillsList(false);
                setKoreanSkillsFocused(false);

                if (selectedKoreanSkills === 'TOPIK certified') {
                  setTopikInfo({
                    type: 'topik',
                    level: '',
                    date: '',
                  });
                } else if (
                  selectedKoreanSkills === 'TOPIK Speaking certified'
                ) {
                  setTopikInfo({
                    type: 'topikSpeaking',
                    level: '',
                    date: '',
                  });
                } else {
                  setTopikInfo(null);
                }
              }}
            />
          </div>
        )}
      </div>

      {topikInfo &&
        (isMobile ? (
          <div className='col-span-full flex flex-col'>
            {(topikInfo.level || topikInfo.date) && (
              <div className='flex flex-col gap-2 pt-2 pb-4 w-full'>
                {topikInfo.level && (
                  <div className='flex items-center gap-2 w-full'>
                    <span className='shrink-0 px-1.5 py-0.5 rounded-[5px] bg-surface-green10 text-green-deep02 border border-surface-green10 typo-b11Strong'>
                      {t('Level', '급수')}
                    </span>
                    <span className='flex-1 min-w-0 typo-b14Strong text-text-placeholder'>
                      {topikInfo.level}
                    </span>
                    <button
                      type='button'
                      onClick={() =>
                        setTopikInfo((prev) => ({ ...prev, level: '' }))
                      }
                      aria-label={t('Delete level', '급수 삭제')}
                      className='shrink-0 w-6 h-6 flex items-center justify-center'
                    >
                      <TrashIcon className='w-5 h-5 text-icon-placeholder' />
                    </button>
                  </div>
                )}

                {topikInfo.date && (
                  <div className='flex items-center gap-2 w-full'>
                    <span className='shrink-0 px-1.5 py-0.5 rounded-[5px] bg-surface-green10 text-green-deep02 border border-surface-green10 typo-b11Strong'>
                      {t('Issue date', '발급일')}
                    </span>
                    <span className='flex-1 min-w-0 typo-b14Strong text-text-placeholder'>
                      {topikInfo.date}
                    </span>
                    <button
                      type='button'
                      onClick={() =>
                        setTopikInfo((prev) => ({ ...prev, date: '' }))
                      }
                      aria-label={t('Delete issue date', '발급일 삭제')}
                      className='shrink-0 w-6 h-6 flex items-center justify-center'
                    >
                      <TrashIcon className='w-5 h-5 text-icon-placeholder' />
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className='group flex px-4 py-3 items-center rounded-lg bg-grayscale-gray02/40 border-grayscale-gray02 border hover:bg-grayscale-gray02/70'>
              <button
                type='button'
                onClick={() => setTopikModalOpen(true)}
                className='flex items-center gap-2 typo-b14Strong text-text-placeholder w-full'
              >
                <span className='flex w-5 h-5 justify-center items-center bg-[hsla(38,9%,82%,0.4)] group-hover:bg-[hsla(38,9%,82%,0.6)] rounded-[10px]'>
                  {topikInfo.date ? (
                    <EditIcon className='w-[14px] h-[14px] text-icon-placeholder' />
                  ) : (
                    <PlusIcon className='w-[14px] h-[14px] text-icon-placeholder' />
                  )}
                </span>
                {topikInfo.date
                  ? t('Edit certification', '자격증 수정')
                  : t('Add certification', '자격증 추가')}
              </button>
            </div>

            {topikModalOpen && (
              <TopikCertModal
                topikInfo={topikInfo}
                setTopikInfo={setTopikInfo}
                onClose={() => setTopikModalOpen(false)}
              />
            )}
          </div>
        ) : (
          <div className='col-span-full relative'>
            <div className='absolute -inset-x-3 -top-3 -bottom-3 bg-green-light01 rounded-b-[20px] rounded-tl-[20px] -z-10 pointer-events-none' />

            <div className='grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-6'>
              {/* TOPIK Level */}
              <div
                ref={
                  topikInfo.type === 'topik'
                    ? topikInputRef
                    : topikSpeakingInputRef
                }
                className='relative min-w-[260px] md:max-w-[380px] w-full'
              >
                <Input
                  label=''
                  placeholder={
                    topikInfo.type === 'topik'
                      ? t('TOPIK Level', 'TOPIK 급수')
                      : t('TOPIK speaking Level', 'TOPIK 말하기 급수')
                  }
                  value={topikInfo.level}
                  readOnly
                  hideCount
                  showStatusIcon={false}
                  state={getInputState(
                    false,
                    topikInfo.type === 'topik'
                      ? isTopikOpen
                      : isTopikSpeakingOpen,
                  )}
                  rightIcon={
                    (
                      topikInfo.type === 'topik'
                        ? isTopikOpen
                        : isTopikSpeakingOpen
                    ) ? (
                      <ArrowTopIcon />
                    ) : (
                      <ArrowBottomIcon />
                    )
                  }
                  onClick={
                    topikInfo.type === 'topik'
                      ? handleTopikClick
                      : handleTopikSpeakingClick
                  }
                  onRightIconClick={
                    topikInfo.type === 'topik'
                      ? handleTopikClick
                      : handleTopikSpeakingClick
                  }
                  className='w-full'
                />

                {isTopikOpen && topikInfo.type === 'topik' && (
                  <div className='relative w-full mt-1.5 tabletH:absolute tabletH:top-[52px] tabletH:left-0 tabletH:mt-0 tabletH:w-full z-10'>
                    <TopikList
                      selectedOption={topikInfo.level}
                      onSelect={(opt) => {
                        setTopikInfo((prev) => ({ ...prev, level: opt }));
                        setIsTopikOpen(false);
                      }}
                    />
                  </div>
                )}

                {isTopikSpeakingOpen && topikInfo.type === 'topikSpeaking' && (
                  <div className='relative w-full mt-1.5 tabletH:absolute tabletH:top-[52px] tabletH:left-0 tabletH:mt-0 tabletH:w-full z-10'>
                    <TopikSpeakingList
                      selectedOption={topikInfo.level}
                      onSelect={(opt) => {
                        setTopikInfo((prev) => ({ ...prev, level: opt }));
                        setIsTopikSpeakingOpen(false);
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Issue date */}
              <div className='relative min-w-[260px] md:max-w-[380px] w-full'>
                <Input
                  id='topik-date-input'
                  label={topikDateFocused ? t('Issue date', '발급일') : ''}
                  placeholder={topikDateFocused ? 'YYYY / MM / DD' : ''}
                  value={topikInfo.date}
                  state={effectiveTopikDateStatus}
                  hideCount
                  showStatusIcon={false}
                  onChange={(e) =>
                    setTopikInfo((prev) => ({ ...prev, date: e.target.value }))
                  }
                  onFocus={() => setTopikDateFocused(true)}
                  onBlur={() => setTopikDateFocused(false)}
                  className='placeholder:opacity-0 w-full'
                />
                {!topikInfo.date && !topikDateFocused && (
                  <span className='absolute left-4 top-[25%] text-text-placeholder pointer-events-none'>
                    {t('Issue date', '발급일')}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}

      {/* Location */}
      <div className='relative min-w-[260px] md:max-w-[380px] w-full'>
        <Input
          id='location-input'
          ref={locationInputRef}
          label={locationFocused ? t('Location *', '거주지 *') : ''}
          placeholder={
            locationFocused
              ? t('Type your city or country', '도시 또는 국가를 입력하세요')
              : ''
          }
          value={location.displayValue}
          onChange={location.handleChange}
          onFocus={location.handleFocus}
          onBlur={location.handleBlur}
          hideCount
          showStatusIcon={false}
          state={effectiveLocationStatus}
          leftIcon={
            locationFocused ? (
              <StyledLocationIcon $state={locationIconState} />
            ) : null
          }
          className='placeholder:opacity-0'
        />

        {!profile.location && !locationFocused && (
          <span className='absolute left-4 top-[25%] text-text-placeholder pointer-events-none'>
            {t('Location', '거주지')}
            <span className='text-text-accent ml-0.5'>*</span>
          </span>
        )}

        {locationFocused && location.displayValue.length >= 2 && (
          <div className='relative w-full mt-1.5 tabletH:absolute tabletH:top-[82px] tabletH:left-0 tabletH:right-0 tabletH:mt-0 z-10'>
            <LocationAutoCompleteList
              searchText={location.displayValue}
              isMobile={isMobile}
              isResume
              onSelect={location.handleSelect}
              onManualEnter={location.handleManualEnter}
            />
          </div>
        )}
      </div>

      {/* Nationality */}
      <NationalityInputBlock
        value={profile.nationality}
        focused={nationalityFocused}
        inputRef={nationalityInputRef}
        state={effectiveNationalityStatus}
        onChange={(e) => updateProfile('nationality', e.target.value)}
        onSelect={(val) => {
          updateProfile('nationality', val);
          setNationalityFocused(false);
        }}
        onFocus={() => {
          closeAllPopups();
          setNationalityFocused(true);
        }}
        onBlur={() => setNationalityFocused(false)}
        isRequired
      />

      {/* Phone */}
      <PhoneInputBlock
        value={profile.phone[0]}
        isActive={isPhoneActive}
        focused={phoneFocused}
        inputRef={phoneInputRef}
        state={effectivePhoneStatus}
        selectedCountry={selectedCountry}
        setSelectedCountry={(country) => {
          setSelectedCountry(country);
          setProfile((prev) => ({
            ...prev,
            phone: prev.phone.map((p, idx) =>
              idx === 0
                ? {
                    ...p,
                    countryCode: country.code,
                    iso: country.iso,
                    countryName: country.name,
                  }
                : p,
            ),
          }));
        }}
        onChange={handlePhoneChange}
        onActivate={() => setIsPhoneActive(true)}
        onDeactivate={() => {
          setIsPhoneActive(false);
          setPhoneFocused(false);
        }}
        onFocus={() => setPhoneFocused(true)}
      />

      {/* Email */}
      <EmailInputBlock
        value={profile.email || ''}
        focused={emailFocused}
        inputRef={emailInputRef}
        state={
          emailStatus === 'warning'
            ? 'warning'
            : emailStatus === 'success'
              ? 'success'
              : effectiveEmailStatus
        }
        message={
          emailStatus === 'warning'
            ? t('Check your email format', '이메일 형식을 확인해 주세요')
            : emailStatus === 'success'
              ? t('All set!', '입력 완료!')
              : ''
        }
        onChange={handleEmailChange}
        onFocus={() => setEmailFocused(true)}
        onBlur={() => {
          setEmailFocused(false);
          setEmailStatus('default');
        }}
        isRequired
      />

      {/* Korean Name */}
      <div className='relative min-w-[260px] md:max-w-[380px] w-full'>
        <Input
          id='korean-name-input'
          label={koreanNameFocused ? t('Korean name', '한국어 이름') : ''}
          value={profile.koreanName}
          state={effectiveKoreanNameStatus}
          hideCount
          showStatusIcon={false}
          onChange={(e) =>
            setProfile((prev) => ({
              ...prev,
              koreanName: e.target.value,
              koreanNameAuto: false,
            }))
          }
          onFocus={() => setKoreanNameFocused(true)}
          onBlur={() => setKoreanNameFocused(false)}
          placeholder=''
          className='placeholder:opacity-0 w-full md:max-w-[380px]'
        />

        {!profile.koreanName && !koreanNameFocused && (
          <span className='absolute left-4 top-[25%] text-text-placeholder pointer-events-none'>
            {t('Korean name', '한국어 이름')}
          </span>
        )}
      </div>

      {/* Career Level */}
      <div
        ref={careerInputRef}
        className='relative min-w-[260px] md:max-w-[380px] w-full'
      >
        <Input
          label=''
          placeholder={
            isCareerOpen
              ? t('Select Career level', '경력 수준 선택')
              : t('Career level', '경력 수준')
          }
          value={(() => {
            const opt = CAREER_OPTIONS.find((o) => o.value === profile.career);
            return opt ? t(opt.label, opt.labelKr) : '';
          })()}
          state={effectiveCareerStatus}
          readOnly
          showStatusIcon={false}
          rightIcon={isCareerOpen ? <ArrowTopIcon /> : <ArrowBottomIcon />}
          onRightIconClick={handleCareerClick}
          onClick={handleCareerClick}
          onBlur={() => {
            setCareerFocused(false);
            setCareerClicked(false);
          }}
          className='placeholder:opacity-0 w-full'
        />

        {/* Career List */}
        {isCareerOpen && (
          <div className='relative w-full mt-1.5 tabletH:absolute tabletH:top-[52px] tabletH:left-0 tabletH:mt-0 z-10'>
            <CareerList
              selectedOption={profile.career || 'Select Career level'}
              onSelect={(option) => {
                updateProfile('career', option);
                setIsCareerOpen(false);
                setCareerFocused(false);
                setCareerClicked(true);
              }}
            />
          </div>
        )}
      </div>

      {dynamicFields.map((field) => {
        const FieldComponent = FIELD_COMPONENT_MAP[field.type];
        if (!FieldComponent) return null;

        const effectiveStatus = getInputState(
          field.value,
          field.focused,
          field.clicked,
        );

        return (
          <div key={field.id} className='relative'>
            <FieldComponent
              inputRef={field.inputRef}
              value={field.value}
              state={effectiveStatus}
              focused={field.focused}
              onActivate={() => {
                updateDynamicField(field.id, {
                  isPhoneActive: true,
                  focused: true,
                });
                setTimeout(() => field.inputRef.current?.focus?.(), 0);
              }}
              selectedCountry={field.selectedCountry}
              setSelectedCountry={(country) =>
                updateDynamicField(field.id, {
                  selectedCountry: country,
                  value: {
                    ...field.value,
                    countryCode: country.code,
                    iso: country.iso,
                    countryName: country.name,
                  },
                })
              }
              onChange={(e) => {
                if (field.type === 'Phone') {
                  updateDynamicField(field.id, {
                    value: {
                      ...field.value,
                      number: e.target.value,
                    },
                  });
                } else {
                  updateDynamicField(field.id, {
                    value: e.target.value,
                  });
                }
              }}
              onFocus={() => {
                closeAllPopups();
                updateDynamicField(field.id, { focused: true });
              }}
              onBlur={() =>
                updateDynamicField(field.id, { focused: false, clicked: false })
              }
              onSelect={(val) => {
                let finalValue;

                if (field.type === 'Phone') {
                  finalValue = val;
                } else if (typeof val === 'object') {
                  finalValue = val.value ?? val.label ?? val.name ?? '';
                } else {
                  finalValue = val;
                }

                updateDynamicField(field.id, {
                  value: finalValue,
                  focused: false,
                  clicked: false,
                });
              }}
              isActive={field.isPhoneActive}
              onDeactivate={() =>
                updateDynamicField(field.id, {
                  focused: false,
                })
              }
            />
          </div>
        );
      })}

      {/* Add Button */}
      <div className='relative'>
        <BaseButton
          type='secondary'
          size='l'
          icon={<PlusIcon />}
          width='100%'
          onClick={addPersonalInfo}
        />
      </div>

      {isModalOpen && (
        <AddPersonalInfoModal
          onClose={() => setIsModalOpen(false)}
          onSave={(selectedOptions) => {
            setDynamicFields((prev) => [
              ...prev,
              ...selectedOptions.map(createFieldInstance),
            ]);
            setIsModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default PersonalInfoForm;
